import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { message } from "antd";

import { marketReferencePriceApi } from "../../../../api/contractManagementApi";
import {
    buildDailyQuoteQueryParams,
    buildSeriesQueryParams,
    buildSourceOptions,
    createDefaultQuoteFilters,
    createDefaultSeriesFilters,
    unwrapApiResponse,
} from "../services/marketReferencePriceViewModel";

const DEFAULT_SERIES_PAGE_SIZE = 8;
const DEFAULT_QUOTE_PAGE_SIZE = 50;

function useMarketReferencePriceData() {
    const [sources, setSources] = useState([]);
    const [sourcesLoading, setSourcesLoading] = useState(false);

    const [seriesFilters, setSeriesFilters] = useState(createDefaultSeriesFilters);
    const [series, setSeries] = useState([]);
    const [seriesLoading, setSeriesLoading] = useState(false);
    const [seriesPagination, setSeriesPagination] = useState({
        current: 1,
        pageSize: DEFAULT_SERIES_PAGE_SIZE,
        total: 0,
    });

    const [selectedSeriesId, setSelectedSeriesId] = useState(null);
    const [selectedSeriesDetail, setSelectedSeriesDetail] = useState(null);
    const [selectedSeriesLoading, setSelectedSeriesLoading] = useState(false);

    const [quoteFilters, setQuoteFilters] = useState(createDefaultQuoteFilters);
    const [quotes, setQuotes] = useState([]);
    const [quotesLoading, setQuotesLoading] = useState(false);
    const [quotePagination, setQuotePagination] = useState({
        current: 1,
        pageSize: DEFAULT_QUOTE_PAGE_SIZE,
        total: 0,
    });

    const seriesFiltersRef = useRef(seriesFilters);
    const seriesPaginationRef = useRef(seriesPagination);
    const selectedSeriesIdRef = useRef(selectedSeriesId);
    const quoteFiltersRef = useRef(quoteFilters);
    const quotePaginationRef = useRef(quotePagination);

    useEffect(() => {
        seriesFiltersRef.current = seriesFilters;
    }, [seriesFilters]);

    useEffect(() => {
        seriesPaginationRef.current = seriesPagination;
    }, [seriesPagination]);

    useEffect(() => {
        selectedSeriesIdRef.current = selectedSeriesId;
    }, [selectedSeriesId]);

    useEffect(() => {
        quoteFiltersRef.current = quoteFilters;
    }, [quoteFilters]);

    useEffect(() => {
        quotePaginationRef.current = quotePagination;
    }, [quotePagination]);

    const loadSources = useCallback(async () => {
        setSourcesLoading(true);

        try {
            const response = await marketReferencePriceApi.getSources({
                page: 1,
                page_size: 200,
                is_active: true,
            });
            const data = unwrapApiResponse(response);
            setSources(data.items || []);
        } catch (error) {
            message.error("加载市场价来源失败");
            console.error(error);
        } finally {
            setSourcesLoading(false);
        }
    }, []);

    const loadSeries = useCallback(async ({
        page = seriesPaginationRef.current.current,
        pageSize = seriesPaginationRef.current.pageSize,
        nextFilters,
    } = {}) => {
        const effectiveFilters = nextFilters ?? seriesFiltersRef.current;
        setSeriesLoading(true);

        try {
            const response = await marketReferencePriceApi.getSeries(
                buildSeriesQueryParams(effectiveFilters, page, pageSize)
            );
            const data = unwrapApiResponse(response);
            const items = data.items || [];

            setSeries(items);
            setSeriesPagination({
                current: data.page || page,
                pageSize: data.page_size || pageSize,
                total: data.total || 0,
            });

            setSelectedSeriesId((current) => {
                if (!items.length) {
                    return null;
                }

                const stillExists = current && items.some((item) => item.id === current);
                return stillExists ? current : items[0].id;
            });

            if (!items.length) {
                setSelectedSeriesDetail(null);
                setQuotes([]);
                setQuotePagination((current) => ({
                    ...current,
                    current: 1,
                    total: 0,
                }));
            }
        } catch (error) {
            message.error("加载市场价曲线失败");
            console.error(error);
        } finally {
            setSeriesLoading(false);
        }
    }, []);

    const loadSelectedSeriesDetail = useCallback(async (seriesId) => {
        if (!seriesId) {
            setSelectedSeriesDetail(null);
            return;
        }

        setSelectedSeriesLoading(true);

        try {
            const response = await marketReferencePriceApi.getSeriesById(seriesId);
            const data = unwrapApiResponse(response);
            setSelectedSeriesDetail(data);
        } catch (error) {
            message.error(error.response?.data?.detail || "加载曲线详情失败");
            console.error(error);
        } finally {
            setSelectedSeriesLoading(false);
        }
    }, []);

    const loadQuotes = useCallback(async ({
        seriesId = selectedSeriesIdRef.current,
        page = quotePaginationRef.current.current,
        pageSize = quotePaginationRef.current.pageSize,
        nextFilters,
    } = {}) => {
        if (!seriesId) {
            setQuotes([]);
            setQuotePagination((current) => ({
                ...current,
                current: 1,
                total: 0,
            }));
            return;
        }

        const effectiveFilters = nextFilters ?? quoteFiltersRef.current;
        setQuotesLoading(true);

        try {
            const response = await marketReferencePriceApi.getDailyQuotes(
                buildDailyQuoteQueryParams(seriesId, effectiveFilters, page, pageSize)
            );
            const data = unwrapApiResponse(response);

            setQuotes(data.items || []);
            setQuotePagination({
                current: data.page || page,
                pageSize: data.page_size || pageSize,
                total: data.total || 0,
            });
        } catch (error) {
            message.error(error.response?.data?.detail || "加载日度报价失败");
            console.error(error);
        } finally {
            setQuotesLoading(false);
        }
    }, []);

    useEffect(() => {
        loadSources();
        loadSeries({ page: 1, pageSize: DEFAULT_SERIES_PAGE_SIZE, nextFilters: seriesFiltersRef.current });
    }, [loadSeries, loadSources]);

    useEffect(() => {
        if (!selectedSeriesId) {
            setSelectedSeriesDetail(null);
            return;
        }

        loadSelectedSeriesDetail(selectedSeriesId);
    }, [loadSelectedSeriesDetail, selectedSeriesId]);

    useEffect(() => {
        if (!selectedSeriesId) {
            setQuotes([]);
            setQuotePagination((current) => ({
                ...current,
                current: 1,
                total: 0,
            }));
            return;
        }

        loadQuotes({
            seriesId: selectedSeriesId,
            page: 1,
            pageSize: quotePaginationRef.current.pageSize,
            nextFilters: quoteFilters,
        });
    }, [loadQuotes, quoteFilters, selectedSeriesId]);

    const searchSeries = useCallback(() => {
        loadSeries({ page: 1, pageSize: seriesPaginationRef.current.pageSize });
    }, [loadSeries]);

    const resetSeriesFilters = useCallback(() => {
        const nextFilters = createDefaultSeriesFilters();
        setSeriesFilters(nextFilters);
        loadSeries({ page: 1, pageSize: seriesPaginationRef.current.pageSize, nextFilters });
    }, [loadSeries]);

    const handleSeriesTableChange = useCallback((nextPagination) => {
        loadSeries({ page: nextPagination.current, pageSize: nextPagination.pageSize });
    }, [loadSeries]);

    const selectSeries = useCallback((seriesId) => {
        setQuoteFilters(createDefaultQuoteFilters());
        setSelectedSeriesId(seriesId);
    }, []);

    const handleQuoteTableChange = useCallback((nextPagination) => {
        loadQuotes({ page: nextPagination.current, pageSize: nextPagination.pageSize });
    }, [loadQuotes]);

    const resetQuoteFilters = useCallback(() => {
        setQuoteFilters(createDefaultQuoteFilters());
    }, []);

    const selectedSeries = useMemo(() => {
        if (selectedSeriesDetail?.id === selectedSeriesId) {
            return selectedSeriesDetail;
        }

        return series.find((item) => item.id === selectedSeriesId) || null;
    }, [selectedSeriesDetail, selectedSeriesId, series]);

    return {
        sources,
        sourcesLoading,
        sourceOptions: buildSourceOptions(sources),
        seriesFilters,
        setSeriesFilters,
        series,
        seriesLoading,
        seriesPagination,
        searchSeries,
        resetSeriesFilters,
        handleSeriesTableChange,
        selectedSeriesId,
        selectSeries,
        selectedSeries,
        selectedSeriesLoading,
        quoteFilters,
        setQuoteFilters,
        resetQuoteFilters,
        quotes,
        quotesLoading,
        quotePagination,
        handleQuoteTableChange,
    };
}

export default useMarketReferencePriceData;
